import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Checkbox, Flex, Skeleton, Typography } from 'antd';
import {
  getShipmentLocationParsedAddress,
  getUserTimeZone,
} from '@/common/utils/utils';
import StatusTag from '@/pages/Shipments/components/StatusTag';
import type { StatusType } from '@/pages/Shipments/statusType';
import TeamListWithDots from '@/pages/Shipments/components/TeamListWithDots';
import type { ShipmentRow } from '../../types';
import {
  formatEstimatedArrival,
  getCustomerReference,
  getLocationName,
  getShipmentRowId,
  getTeams,
  isPlaceholderRow,
  textOrEmpty,
} from './shipmentTableUtils';
import './shipments.css';
import dayjs from 'dayjs';
import { trackingPillStyle } from './ShipmentTableCells';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';

interface ShipmentMobileCardsProps {
  shipments: ShipmentRow[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  openShipmentId?: string | null;
  highlightedShipmentId?: string | null;
  onOpenShipment: (id: string, shipment: ShipmentRow) => void;
  onLoadMore?: () => void;
  customerLegEnabled?: boolean;

  checked?: boolean;
  selectedIds?: string[];
  excludedIds?: string[];
  isSelectAllMode?: boolean;
  onSelectChange?: (
    id: string,
    checked: boolean,
    shipment: ShipmentRow
  ) => void;
}

export const getCurrentStage = (record: ShipmentRow): string => {
  if (
    typeof record.custom_status === 'object' &&
    !Array.isArray(record.custom_status)
  ) {
    return textOrEmpty(record.custom_status?.current_stage);
  }

  return textOrEmpty(record.status);
};

const getStages = (record: ShipmentRow): unknown[] => {
  if (
    typeof record.custom_status === 'object' &&
    !Array.isArray(record.custom_status) &&
    Array.isArray(record.custom_status?.stages)
  ) {
    return record.custom_status.stages;
  }

  return [];
};

const ShipmentMobileCards: React.FC<ShipmentMobileCardsProps> = ({
  shipments,
  loading,
  loadingMore,
  hasMore,
  openShipmentId,
  highlightedShipmentId,
  onOpenShipment,
  onLoadMore,
  customerLegEnabled = false,

  checked,
  selectedIds,
  excludedIds,
  isSelectAllMode,
  onSelectChange,
}) => {
  const { dateFormat } = useUserDateTimeFormat();
  const isLoadingRef = useRef(loading || loadingMore);
  const hasMoreRef = useRef(hasMore);
  const onLoadMoreRef = useRef(onLoadMore);

  const renderableShipments = useMemo(
    () => shipments.filter((shipment) => !isPlaceholderRow(shipment)),
    [shipments]
  );

  useEffect(() => {
    isLoadingRef.current = loading || loadingMore;
    hasMoreRef.current = hasMore;
    onLoadMoreRef.current = onLoadMore;
  }, [hasMore, loading, loadingMore, onLoadMore]);

  const observerRef = useRef<IntersectionObserver | null>(null);

  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (!hasMoreRef.current || isLoadingRef.current) return;

        onLoadMoreRef.current?.();
      },
      {
        root: null,
        rootMargin: '340px 0px',
        threshold: 0.01,
      }
    );

    observerRef.current.observe(node);
  }, []);

  if (loading && renderableShipments.length === 0) {
    return (
      <div className='shipment-mobile-card-list'>
        {Array.from({ length: 4 }, (_, index) => (
          <div className='shipment-mobile-card mt-2' key={index}>
            <Skeleton active paragraph={{ rows: 5 }} title />
          </div>
        ))}
      </div>
    );
  }

  if (renderableShipments.length === 0) {
    return <div className='shipment-mobile-empty'>No data</div>;
  }

  return (
    <div className='shipment-mobile-card-list mt-2'>
      {renderableShipments.map((shipment) => {
        const rowId = getShipmentRowId(shipment);
        const teams = getTeams(shipment);
        const stages = getStages(shipment);
        const estimatedArrival = formatEstimatedArrival(
          shipment.estimated_delivery_date,
          shipment?.eta_timezone || shipment?.destination_timezone,
          shipment?.status,
          dateFormat
        );
        const providerText = textOrEmpty(shipment.courier_name);
        const isActive =
          openShipmentId === rowId || highlightedShipmentId === rowId;
        const isDelivered =
          shipment?.status?.trim()?.toLowerCase() === 'delivered';
        const selected = isSelectAllMode
          ? !excludedIds.includes(rowId)
          : selectedIds.includes(rowId);
        return (
          <article
            key={rowId}
            className={`shipment-mobile-card${isActive ? ' is-active' : ''}`}
            role='button'
            tabIndex={0}
            onClick={() => onOpenShipment(rowId, shipment)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenShipment(rowId, shipment);
              }
            }}
          >
            {checked && (
              <div
                className='mobile-card-checkbox'
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                onDoubleClick={(event) => event.stopPropagation()}
              >
                <Checkbox
                  checked={selected}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    event.nativeEvent.stopPropagation();
                    onSelectChange(rowId, event.target.checked, shipment);
                  }}
                />
              </div>
            )}

            <div>
              <div>
                <Typography.Text
                  ellipsis={{ tooltip: getCustomerReference(shipment) }}
                  className={'shipment-mobile-card-reference'}
                >
                  {getCustomerReference(shipment)}
                </Typography.Text>
              </div>

              {teams.length > 0 ? (
                <div className='shipment-mobile-card-teams'>
                  <TeamListWithDots teams={teams} />
                </div>
              ) : null}

              <Flex
                className='shipment-mobile-card-status-row'
                align='center'
                gap={8}
                wrap='wrap'
              >
                {isDelivered ? (
                  <StatusTag
                    type={3}
                    status='Delivered'
                    alert_status={[]}
                    className='shipment-grid-status-tag shipment-grid-delivered-tag'
                    style={{ borderRadius: 4, ...trackingPillStyle }}
                  />
                ) : (
                  <span className='shipment-mobile-card-status'>
                    {getCurrentStage(shipment)}
                  </span>
                )}
                {Array.isArray(shipment?.alert_status) &&
                shipment.alert_status.length > 0 ? (
                  <StatusTag
                    type={shipment.type as StatusType}
                    status={shipment.status as string}
                    alert_status={shipment.alert_status as string[]}
                    customerLegEnabled={customerLegEnabled}
                    className='shipment-mobile-card-alert'
                    truncate
                    style={{
                      borderRadius: 4,
                      width: 'fit-content',
                      ...trackingPillStyle,
                    }}
                    textStyle={{ fontSize: 10, fontWeight: 600 }}
                  />
                ) : null}
              </Flex>

              <Flex gap={8} align='center'>
                {providerText && (
                  <div className='shipment-mobile-card-provider'>
                    {providerText}
                  </div>
                )}

                {providerText && estimatedArrival && '∙'}

                {estimatedArrival && (
                  <div className='shipment-mobile-card-provider'>
                    Estimated arrival {estimatedArrival}
                  </div>
                )}
              </Flex>

              {stages.length > 0 ? (
                <div
                  className='shipment-mobile-card-progress'
                  style={
                    { '--stage-count': stages.length } as React.CSSProperties
                  }
                >
                  {stages.map((stage, index) => (
                    <span
                      key={`${(stage as { key?: string })?.key || index}`}
                      className={
                        isDelivered
                          ? 'is-delivered'
                          : (stage as { is_completed?: boolean })?.is_completed
                            ? 'is-complete'
                            : ''
                      }
                    />
                  ))}
                </div>
              ) : null}

              <div className='shipment-mobile-card-locations'>
                <ShipmentMobileLocation shipment={shipment} type='from' />
                <ShipmentMobileLocation shipment={shipment} type='to' />
              </div>
            </div>
          </article>
        );
      })}

      <div ref={loadMoreRef} className='shipment-mobile-card-sentinel'>
        {loadingMore ? (
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        ) : null}
      </div>
    </div>
  );
};

export const formatShipmentDate = (dateStr: string, userTz: string) => {
  if (!dateStr) return;

  return dayjs(dateStr)
    .tz(userTz) // convert to user timezone
    .format('M/D/YY h:mm A z'); // z = timezone abbreviation
};

const ShipmentMobileLocation: React.FC<{
  shipment: ShipmentRow;
  type: 'from' | 'to';
}> = ({ shipment, type }) => {
  const isOrigin = type === 'from';
  const key = isOrigin ? 'from_location' : 'to_location';
  const date = isOrigin
    ? shipment.actual_pickup_date || shipment.scheduled_pickup_date
    : shipment.actual_delivery_date || shipment.scheduled_delivery_date;

  const userTimeZone = getUserTimeZone()?.name;

  const formattedDate = formatShipmentDate(String(date ?? ''), userTimeZone);

  const location = shipment[key];
  const renderLocation = getShipmentLocationParsedAddress(location as never);

  return (
    <div className='shipment-mobile-card-location'>
      <Typography.Text
        className='shipment-mobile-card-location-title'
        ellipsis={{
          tooltip: getLocationName(
            location,
            isOrigin ? 'Origin' : 'Destination'
          ),
        }}
      >
        {getLocationName(location, isOrigin ? 'Origin' : 'Destination')}
      </Typography.Text>

      <Typography.Text
        className={`shipment-mobile-card-location-address ${!renderLocation ? 'when-unknown' : ''}`}
        ellipsis={{
          tooltip: renderLocation,
        }}
      >
        {renderLocation ? renderLocation : 'Unknown'}
      </Typography.Text>

      <Typography.Text
        className={`shipment-mobile-card-location-date ${!formattedDate ? 'when-unknown' : ''}`}
      >
        {textOrEmpty(formattedDate ? formattedDate : 'Unknown')}
      </Typography.Text>
    </div>
  );
};

export default ShipmentMobileCards;
